import React, { ReactElement, useCallback, useState } from "react";
import "./index.less";
import { CheckboxOptionType, Image } from "antd";
import { VideoWindowLayoutTypes } from "remo-types/src/constants";
import { useTranslate } from "@obsbot/remo-i18n";
import EqualPNG from "./imgs/equal.png";
import BottomPNG from "./imgs/bottom.png";
import TopPNG from "./imgs/top.png";
import { useGlobalStore, usePreferencesStore } from "@obsbot/remo-stores";
import { eventBus } from "@obsbot/remo-utils";

const LayoutItem = ({
    text,
    img,
    radio,
}: {
    text: string;
    img: string;
    radio?: ReactElement;
}): React.ReactElement => {
    return (
        <div className="layout-item">
            <Image preview={false} src={img} />
            <div className="layout-item-text">
                {radio && radio}
                {text}
            </div>
        </div>
    );
};

export interface LayoutTypeProps {}
export const LayoutType: React.FC<LayoutTypeProps> = () => {
    const t = useTranslate();
    const globalStore = useGlobalStore();
    const preferenceStore = usePreferencesStore(globalStore.userInfo?.user_id ?? "")?.(
        state => state,
    );
    const [currentLayout, setCurrentLayout] = useState<VideoWindowLayoutTypes>(
        preferenceStore?.channelLayoutType ?? VideoWindowLayoutTypes.Equal,
    );

    const onChange = (curType: string | number): void => {
        setCurrentLayout(curType as VideoWindowLayoutTypes);
        preferenceStore?.updateChannelLayoutType(curType as VideoWindowLayoutTypes);
        eventBus.emit("channel-layout-type-changed", undefined);
    };

    return (
        <div className="layout-type">
            <RadioGroup
                renderOptions={radioEl => {
                    return [
                        {
                            label: (
                                <label
                                    htmlFor={VideoWindowLayoutTypes.Equal}
                                    style={{ cursor: "pointer" }}
                                >
                                    <LayoutItem
                                        img={EqualPNG}
                                        radio={radioEl?.(VideoWindowLayoutTypes.Equal)}
                                        text={t("channel.layout-equal")}
                                    />
                                </label>
                            ),
                            value: VideoWindowLayoutTypes.Equal,
                        },
                        {
                            label: (
                                <label
                                    htmlFor={VideoWindowLayoutTypes.Bottom}
                                    style={{ cursor: "pointer" }}
                                >
                                    <LayoutItem
                                        img={BottomPNG}
                                        radio={radioEl?.(VideoWindowLayoutTypes.Bottom)}
                                        text={t("channel.layout-bottom")}
                                    />
                                </label>
                            ),
                            value: VideoWindowLayoutTypes.Bottom,
                        },
                        {
                            label: (
                                <label
                                    htmlFor={VideoWindowLayoutTypes.Top}
                                    style={{ cursor: "pointer" }}
                                >
                                    <LayoutItem
                                        img={TopPNG}
                                        radio={radioEl?.(VideoWindowLayoutTypes.Top)}
                                        text={t("channel.layout-top")}
                                    />
                                </label>
                            ),
                            value: VideoWindowLayoutTypes.Top,
                        },
                    ];
                }}
                value={currentLayout}
                onChange={onChange}
            />
        </div>
    );
};

type CustomRadioOption = string | number | CheckboxOptionType<any>;
export const RadioGroup: React.FC<{
    options?: CustomRadioOption[];
    className?: string;
    value: string | number;
    renderOptions?: (renderEl?: (opt: string | number) => ReactElement) => CustomRadioOption[];
    onChange?: (value: string | number) => void;
}> = ({ renderOptions, options, value, onChange, className = "" }) => {
    const key = useCallback((option: CustomRadioOption) => {
        return typeof option === "object" ? option.value : option;
    }, []);
    const label = useCallback((option: CustomRadioOption) => {
        return typeof option === "object" ? option.label : option;
    }, []);
    const radioEl = (key: string | number): ReactElement => {
        return (
            <input
                checked={value === key}
                className="ant-radio"
                id={key as string}
                name="custom-radio-group"
                type="radio"
                value={value}
                // @ts-ignore
                onChange={() => onChange?.(key)}
            />
        );
    };
    return (
        <div className={`ant-radio-group ${className}`}>
            {renderOptions
                ? renderOptions(radioEl).map(opt => (
                      <div
                          key={key(opt)}
                          className={
                              "ant-radio-wrapper" +
                              (value === key(opt) ? " ant-radio-wrapper-checked" : "")
                          }
                      >
                          <span className="ant-radio-label">{label(opt)}</span>
                      </div>
                  ))
                : options?.map(opt => (
                      <div
                          key={key(opt)}
                          className={
                              "ant-radio-wrapper" +
                              (value === key(opt) ? " ant-radio-wrapper-checked" : "")
                          }
                      >
                          <span className="ant-radio-label">{label(opt)}</span>
                          <label key={key(opt)} htmlFor={key(opt)}>
                              {radioEl(key(opt))}
                          </label>
                      </div>
                  ))}
        </div>
    );
};
